import {ChangeDetectionStrategy, Component, computed, inject, input, LOCALE_ID, output} from '@angular/core';
import {TranslatePipe} from '@ngx-translate/core';
import {DatePipe} from '@angular/common';
import {AiActionStatus, AiActionType, AiMessageView, ProposedAction} from '../../../../../../../api';
import {ElciaBadge} from '../../../../../../components/badge/badge';
import {ElciaButton} from '../../../../../../components/buttons/button/button';

interface ActionField {
  label: string;

  /** Single-line value; empty when the field renders `items` instead. */
  value: string;

  /** Multi-value field rendered as a bullet list. */
  items?: string[];

  /** La valeur est une clé de traduction (valeur d'énumération) et non un texte déjà lisible. */
  isKey?: boolean;
}

@Component({
  selector: 'elcia-ai-action-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, ElciaButton, ElciaBadge],
  templateUrl: './ai-action-card.html',
  styleUrl: './ai-action-card.scss',
})
export class AiActionCard {

  readonly message = input.required<AiMessageView>();

  readonly confirmed = output<AiMessageView>();
  readonly cancelled = output<AiMessageView>();

  readonly isBusy = input(false);

  protected readonly action = computed<ProposedAction | undefined>(() => this.message().action ?? undefined);
  protected readonly isPending = computed(() => this.message().actionStatus === AiActionStatus.PENDING);
  protected readonly isConfirmed = computed(() => this.message().actionStatus === AiActionStatus.CONFIRMED);

  // Le client et les assignés sont affichés en clair : c'est ici que l'utilisateur repère
  // un mauvais rattachement avant de valider.
  private readonly datePipe = new DatePipe(inject(LOCALE_ID));

  protected readonly fields = computed<ActionField[]>(() => {
    const action = this.action();
    if (!action) return [];

    // Sur un ajout de sous-tâche, title porte le libellé de la sous-tâche, pas celui du parent.
    const isAddSubTask = action.type === AiActionType.ADDSUBTASK;

    // statusName arrive déjà traduit par le serveur (traduction par colonnes) ; priorité et
    // avancement sont des valeurs d'énumération, traduites ici par leur clé.
    return [
      {label: 'ai.field.todo', value: action.todoTitle ?? ''},
      {label: 'ai.field.title', value: isAddSubTask ? '' : action.title ?? ''},
      {label: 'ai.field.description', value: isAddSubTask ? '' : action.description ?? ''},
      {label: 'ai.field.due-date', value: this.formatDate(action.dueDate)},
      {label: 'ai.field.priority', value: this.key('todo-priority.', action.priority), isKey: true},
      {label: 'ai.field.status', value: action.statusName ?? ''},
      {label: 'ai.field.completion', value: this.key('todo-completion.', action.completion), isKey: true},
      {label: 'ai.field.assignees', value: (action.assigneeNames ?? []).join(', ')},
      {label: 'ai.field.client', value: action.concernName ?? ''},
      {
        label: 'ai.field.sub-tasks',
        value: '',
        items: isAddSubTask
          ? action.title ? [action.title] : []
          : action.subTasks ?? [],
      },
      {label: 'todo.estimated-time-in-days', value: action.estimatedTimeInDays?.toString() ?? ''},
      // Une tâche privée est invisible pour les collègues : on ne l'affiche que si elle l'est.
      {label: 'todo.private', value: action.isPrivate ? 'common.yes' : '', isKey: true},
    ].filter(field => field.value !== '' || (field.items?.length ?? 0) > 0);
  });

  private key(prefix: string, value: string | null | undefined): string {
    return value ? prefix + value : '';
  }

  private formatDate(date: Date | string | null | undefined): string {
    return date ? this.datePipe.transform(date, 'dd/MM/yyyy') ?? '' : '';
  }
}
